import { Divider, Group, Paper, Stack } from "@mantine/core"
import type { PropsWithChildren, ReactNode } from "react"
import { Score } from "@/components/utils"
import type { TQuestionType } from "@/types"
import { AnswerBoolean } from "./AnswerBoolean"
import { AnswerComment } from "./AnswerComment"
import { AnswerDate } from "./AnswerDate"
import { AnswerLikert } from "./AnswerLikert"
import { AnswerMultiple } from "./AnswerMultiple"
import { AnswerPersonal } from "./AnswerPersonal"
import { AnswerScale } from "./AnswerScale"
import { AnswerSelect } from "./AnswerSelect"
import { AnswerTags } from "./AnswerTags"
import { NoAnswer } from "./NoAnswer"
import type { IAnswerProps } from "./types"
import { isNumericType, isStringType } from "./utils"

export function Answer({ children, question, ...props }: PropsWithChildren<IAnswerProps>) {
  const isStringValue = isStringType(question)
  const isNumericValue = !isStringValue && isNumericType(question)
  const { answer } = props

  return (
    <Paper withBorder p="xs" data-testid={`answer-${answer?.id}`}>
      <Stack align="flex-start" gap="xs">
        <Group wrap="nowrap" justify="space-between" align="center" gap={4} w="100%">
          {children}
          {isNumericValue && <AnswerValue question={question} {...props} />}
        </Group>
        {!isStringValue && !isNumericValue && <AnswerValue question={question} {...props} />}
        <AnswerComment question={question} placeholder={isStringValue ? <NoAnswer /> : null} {...props}>
          <Divider variant="solid" />
        </AnswerComment>
      </Stack>
    </Paper>
  )
}

function AnswerValue(props: IAnswerProps) {
  const Render = renders[props.question.type]
  if (!Render) {
    console.warn(`No render found for question type: ${props.question.type}`)
    return null
  }
  return <Render {...props} />
}

const renders: Partial<Record<TQuestionType, (props: IAnswerProps) => ReactNode>> = {
  boolean: AnswerBoolean,
  scale: AnswerScale,
  likert: AnswerLikert,
  // TODO: add support for other types
  tags: AnswerTags,
  date: AnswerDate,
  number: ({ answer }) => <Score value={answer?.value} base={100} rate={false} fixed={false} />,
  select: AnswerSelect,
  multiple: AnswerMultiple,
  personal: AnswerPersonal,
}
